import type React from 'react';
import type { MessageAttachment } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { useTranslation } from '../../../../lib/i18n';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import { useAudioPlayer } from '../../../../lib/use-audio-player';
import { attachmentPalette, type AttachmentTone } from './palette';

// Inline player bubble for an audio/* attachment (voice notes included):
// play/pause + progress + duration, custom minimal UI instead of the browser
// controls bar.
//
// The playback half lives in `lib/use-audio-player.ts`, shared with the doc viewer's `AudioDocView`. Only
// the CHROME is local to this file — the one-at-a-time playback lock is module state in the hook, so a track
// started in the viewer pauses a voice note playing here and vice versa.
export function AudioAttachmentPlayer({ attachment, tone }: { attachment: MessageAttachment; tone: AttachmentTone }) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const palette = attachmentPalette(tone);
  const player = useAudioPlayer(() => logPageClickBi({ page_name: 'chat', button_name: 'voice_note_played' }));
  const { playing, progress, timeLabel } = player;

  return (
    <div role="group" aria-label={t('chat.voice.voiceNote')} style={styles.wrap(palette)}>
      <audio {...player.audioProps} src={attachment.url} style={{ display: 'none' }} />
      <button
        type="button"
        onClick={player.toggle}
        aria-label={playing ? t('common.stop') : t('chat.voice.voiceNote')}
        title={t('chat.voice.voiceNote')}
        style={styles.playButton(palette)}
      >
        {playing ? <PauseIcon /> : <PlayIcon />}
      </button>
      <div onClick={player.seek} style={styles.track} aria-hidden>
        <div style={styles.trackRail(palette)} />
        <div style={styles.trackFill(palette, progress)} />
      </div>
      <span style={styles.time(palette)}>{timeLabel}</span>
    </div>
  );
}

function PlayIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden>
      <path d="M3.2 1.8v8.4c0 .5.55.8.97.53l6.1-4.2a.63.63 0 0 0 0-1.06l-6.1-4.2a.63.63 0 0 0-.97.53z" fill="currentColor" />
    </svg>
  );
}

function PauseIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden>
      <rect x="2.4" y="1.8" width="2.6" height="8.4" rx="1" fill="currentColor" />
      <rect x="7" y="1.8" width="2.6" height="8.4" rx="1" fill="currentColor" />
    </svg>
  );
}

type Palette = ReturnType<typeof attachmentPalette>;

const styles = {
  wrap: (palette: Palette): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginTop: 8,
    padding: '8px 12px',
    background: palette.tileBg,
    border: palette.tileBorder,
    borderRadius: 'var(--sym-radius-badge)',
    width: 240,
    maxWidth: '100%',
  }),
  playButton: (palette: Palette): React.CSSProperties => ({
    flexShrink: 0,
    width: 28,
    height: 28,
    borderRadius: '50%',
    border: 'none',
    background: palette.badgeBg,
    color: palette.fg,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  }),
  track: {
    position: 'relative',
    flex: 1,
    minWidth: 0,
    height: 16,
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
  } satisfies React.CSSProperties,
  trackRail: (palette: Palette): React.CSSProperties => ({
    position: 'absolute',
    insetInline: 0,
    height: 3,
    borderRadius: 2,
    background: palette.badgeBg,
  }),
  trackFill: (palette: Palette, progress: number): React.CSSProperties => ({
    position: 'absolute',
    insetInlineStart: 0,
    width: `${Math.round(progress * 100)}%`,
    height: 3,
    borderRadius: 2,
    background: palette.fg,
    transition: 'width 120ms linear',
  }),
  time: (palette: Palette): React.CSSProperties => ({
    flexShrink: 0,
    fontSize: 11,
    fontVariantNumeric: 'tabular-nums',
    color: palette.fg,
    opacity: 0.75,
  }),
};
